import {
    InstalledBaseNodeUpdateAttributesInput,
    InstalledBaseNodeCreateAttributesInput
} from "../codegen/graphql";
import { TypeDefinitionAttribute } from "../types";

/**
 * Custom attribute input for GraphQL mutations.
 * Uses either 'value' (single) or 'values' (multi) based on the TDA 'list' property.
 */
export interface CustomAttributeInput {
    attributeIndex: string;
    value?: string;      // For single-value attributes (list: false)
    values?: string[];   // For multi-value attributes (list: true)
}

/**
 * Result of separating form values into standard input and dynamic list groups.
 * `dynamicLists` is keyed by the TDA's `dynamicFilterListKey` value (e.g. "attributes").
 */
export interface SeparatedNodeAttributes {
    input: InstalledBaseNodeUpdateAttributesInput;
    dynamicLists: Record<string, CustomAttributeInput[]>;
}

/**
 * Separates form values into standard attributes and dynamic list attributes
 * for the updateInstalledBaseNode mutation.
 *
 * Standard attributes are fields without `dynamicFilterListKey` that match
 * InstalledBaseNodeUpdateAttributesInput fields.
 * Dynamic list attributes are grouped by their `dynamicFilterListKey` value
 * and built as CustomAttributeInput arrays.
 */
export const separateInstalledBaseNodeAttributes = (
    formValues: { [key: string]: any },
    attributes: TypeDefinitionAttribute[]
): SeparatedNodeAttributes => {
    const standardFields: Array<keyof InstalledBaseNodeUpdateAttributesInput> = [
        'key',
        'title',
        'clientAddressId',
        'servicePool',
        'serviceUnitId',
        // File-service UUID of the node image. An absent key leaves the image
        // untouched on the backend, so callers must strip it when unchanged
        // (see prepareNodeImageFormValues) — '' → null below means "unlink".
        'imageId'
    ];

    const { input, dynamicLists } = separateByDynamicKey(formValues, attributes, standardFields);

    return { input, dynamicLists };
};

/**
 * Result of separating form values for create operation
 */
export interface SeparatedNodeCreateAttributes {
    input: InstalledBaseNodeCreateAttributesInput;
    dynamicLists: Record<string, CustomAttributeInput[]>;
}

/**
 * Separates form values into standard attributes and dynamic list attributes
 * for the createInstalledBaseNode mutation.
 */
export const separateInstalledBaseNodeCreateAttributes = (
    formValues: { [key: string]: any },
    attributes: TypeDefinitionAttribute[]
): SeparatedNodeCreateAttributes => {
    const standardFields: Array<keyof InstalledBaseNodeCreateAttributesInput> = [
        'title',
        'nodeTypeKey',
        'servicePool',
        'imageId'
    ];

    const { input, dynamicLists } = separateByDynamicKey(formValues, attributes, standardFields);

    return { input, dynamicLists };
};

/**
 * Shared logic: separates form values into standard input fields and
 * dynamic list groups based on each attribute's `dynamicFilterListKey`.
 */
const separateByDynamicKey = (
    formValues: { [key: string]: any },
    attributes: TypeDefinitionAttribute[],
    standardFields: string[]
): { input: any; dynamicLists: Record<string, CustomAttributeInput[]> } => {
    // Build input object with only standard fields
    const input: any = {};
    standardFields.forEach(field => {
        if (formValues[field] !== undefined) {
            input[field] = formValues[field] === '' ? null : formValues[field];
        }
    });

    // Group attributes by dynamicFilterListKey
    const dynamicLists: Record<string, CustomAttributeInput[]> = {};

    for (const attr of attributes) {
        if (!attr.dynamicFilterListKey) continue;

        const value = formValues[attr.index];
        if (value === undefined) continue;

        const customAttr = buildCustomAttributeInput(attr.index, value, attr);
        if (!customAttr) continue;

        if (!dynamicLists[attr.dynamicFilterListKey]) {
            dynamicLists[attr.dynamicFilterListKey] = [];
        }
        dynamicLists[attr.dynamicFilterListKey].push(customAttr);
    }

    return { input, dynamicLists };
};

/**
 * Builds a CustomAttributeInput based on the attribute metadata.
 * Uses 'values' for multi-value attributes (list: true), 'value' for single-value.
 *
 * @param index - The attribute index
 * @param value - The form value
 * @param attr - The attribute metadata
 * @returns CustomAttributeInput or null if value should be skipped
 */
const buildCustomAttributeInput = (
    index: string,
    value: any,
    attr: TypeDefinitionAttribute
): CustomAttributeInput | null => {
    // Handle null/empty values - signal deletion with empty value
    if (value === null || value === '') {
        return {
            attributeIndex: index,
            value: ''
        };
    }

    // Multi-value attribute (list: true)
    if (attr.list === true) {
        if (Array.isArray(value)) {
            return {
                attributeIndex: index,
                values: value.map(v => convertValueToString(v))
            };
        }
        // Single value provided for multi-value field - wrap in array
        return {
            attributeIndex: index,
            values: [convertValueToString(value)]
        };
    }

    // Single-value attribute (list: false or undefined)
    if (Array.isArray(value)) {
        // Array provided for single-value field - join or take first
        return {
            attributeIndex: index,
            value: value.length > 0 ? convertValueToString(value[0]) : ''
        };
    }

    return {
        attributeIndex: index,
        value: convertValueToString(value)
    };
};

/**
 * Helper function to convert various types to string for GraphQL
 * @param value - The value to convert
 * @returns String representation of the value
 */
// @ts-ignore
const convertValueToString = (value: any): string => {
    if (typeof value === 'string') return value;
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'number') return value.toString();
    if (Array.isArray(value)) return JSON.stringify(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};
